import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import type { ExportReportType } from '../../domains/export/models/ExportRequest';
import type { ExportJobRecord } from '../../domains/export/models/ExportRequest';
import { useExportProgress } from '../../hooks/export/useExportProgress';
import { Tracker } from '../../domains/analytics/services/Tracker';
import { REPORT_EXPORT } from '../../domains/analytics/constants/MetricEvents';
import { useDemoStatus } from '../../hooks/useAuth';
import { useAuthStore } from '../../stores/authStore';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useUpgradeModalStore } from '../../stores/upgradeModalStore';
import { OPEN_ONBOARDING_EVENT, Pages } from '../modals/OnboardingDialog';
import {
  getReportExportAccess,
  type ReportExportAccess,
} from '../../domains/export/services/genogramReportEntitlements';
import { isPlanUpgradeError } from '../../domains/shared/utils/planUpgradeError';

interface ReportOption {
  id: ExportReportType;
  title: string;
  description: string;
  reportVariant?: string;
}

interface ReportExportModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onStart: (selection: {
    option: ReportOption;
    format: 'docx' | 'pdf' | 'csv' | 'json';
    progressSince?: string;
  }) => Promise<void> | void;
  isSubmitting?: boolean;
  job?: ExportJobRecord | null;
  error?: Error | null;
  isSaving?: boolean;
  isDownloading?: boolean;
  onCancel?: () => void;
}

export const ReportExportModal: React.FC<ReportExportModalProps> = ({
  open,
  onOpenChange,
  onStart,
  isSubmitting = false,
  job,
  error,
  isSaving = false,
  isDownloading = false,
  onCancel,
}) => {
  const { t } = useTranslation('diagram');
  const { t: tCommon } = useTranslation('common');
  const [selectedReportId, setSelectedReportId] = useState<ExportReportType | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<'docx' | 'pdf' | 'csv' | 'json'>('docx');
  const [progressSinceDate, setProgressSinceDate] = useState('');
  const [requestError, setRequestError] = useState<string | null>(null);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { entitlements, loading: entitlementsLoading } = useEntitlements();
  const demoStatus = useDemoStatus();
  const isDemoUser = demoStatus?.toUpperCase() === 'DEMO';
  const { openModal: openUpgradeModal } = useUpgradeModalStore();
  const upgradeTimerRef = useRef<number | null>(null);

  const REPORT_OPTIONS: ReportOption[] = [
    {
      id: 'genogram.clinical_case',
      title: t('export.clinicalCaseFile'),
      description: t('export.clinicalDescription'),
    },
    {
      id: 'genogram.health_risk_summary',
      title: t('export.healthRiskSummary'),
      description: t('export.healthRiskDescription'),
    },
    {
      id: 'genogram.legal_forensic',
      title: t('export.legalForensicTitle'),
      description: t('export.legalForensicDescription'),
    },
    {
      id: 'genogram.relationship_dynamics',
      title: t('export.relationshipDynamicsTitle'),
      description: t('export.relationshipDynamicsDescription'),
    },
    {
      id: 'genogram.genealogical_historical',
      title: t('export.genealogicalHistoricalTitle'),
      description: t('export.genealogicalHistoricalDescription'),
    },
    {
      id: 'genogram.custom_analytical',
      title: t('export.customAnalyticalTitle'),
      description: t('export.customAnalyticalDescription'),
    },
    {
      id: 'genogram.session_progress_snapshot',
      title: t('export.sessionProgressTitle'),
      description: t('export.sessionProgressDescription'),
    },
    {
      id: 'genogram.anonymized_teaching',
      title: t('export.anonymizedTeachingTitle'),
      description: t('export.anonymizedTeachingDescription'),
    },
  ];

  const getReportUpgradeMessage = useCallback(
    () => t('export.reportCapabilityRequired'),
    [t],
  );

  const resolveReportUpgradeFeature = useCallback(
    (capability: string | null | undefined) => capability ?? 'genogram.report',
    [],
  );

  const openUpgradeForReportAccess = useCallback(
    (access: ReportExportAccess, fallbackFeature?: string) => {
      const feature = resolveReportUpgradeFeature(access.requiredCapability ?? fallbackFeature);
      onOpenChange(false);
      if (upgradeTimerRef.current !== null) {
        window.clearTimeout(upgradeTimerRef.current);
      }
      upgradeTimerRef.current = window.setTimeout(() => {
        openUpgradeModal(feature, getReportUpgradeMessage());
        upgradeTimerRef.current = null;
      }, 50);
    },
    [getReportUpgradeMessage, onOpenChange, openUpgradeModal, resolveReportUpgradeFeature],
  );

  const resolveReportAccess = useCallback(
    (reportType: ExportReportType): ReportExportAccess => {
      const access = getReportExportAccess(entitlements, reportType);
      if (isDemoUser) {
        return {
          ...access,
          allowed: false,
        };
      }
      return access;
    },
    [entitlements, isDemoUser],
  );

  const reportAccessById = useMemo(() => {
    const reportTypes: ExportReportType[] = [
      'genogram.clinical_case',
      'genogram.health_risk_summary',
      'genogram.legal_forensic',
      'genogram.relationship_dynamics',
      'genogram.genealogical_historical',
      'genogram.custom_analytical',
      'genogram.session_progress_snapshot',
      'genogram.anonymized_teaching',
    ];
    const accessMap = new Map<ExportReportType, ReportExportAccess>();
    for (const reportType of reportTypes) {
      accessMap.set(reportType, resolveReportAccess(reportType));
    }
    return accessMap;
  }, [resolveReportAccess]);

  const selectedReportAccess = selectedReportId ? reportAccessById.get(selectedReportId) ?? null : null;
  const isSelectedReportBlocked = selectedReportAccess ? !selectedReportAccess.allowed : false;

  useEffect(() => {
    if (!open) return;
    setSelectedReportId(null);
    setSelectedFormat('docx');
    setProgressSinceDate('');
    setRequestError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    Tracker.track(REPORT_EXPORT.MODAL_OPENED, {});
  }, [open]);

  useEffect(() => {
    if (!error) return;
    setRequestError(error.message);
  }, [error]);

  const apiUpgradeFeature = error && isPlanUpgradeError(error)
    ? resolveReportUpgradeFeature(error.feature)
    : null;
  const apiUpgradeAccess: ReportExportAccess | null = error && isPlanUpgradeError(error)
    ? {
        allowed: false,
        requiredCapability: null,
        requiredAddOn: null,
      }
    : null;

  useEffect(() => () => {
    if (upgradeTimerRef.current !== null) {
      window.clearTimeout(upgradeTimerRef.current);
    }
  }, []);

  const {
    progress,
    showProgress,
    statusMessage,
    isFailed,
    hasActiveExport,
  } = useExportProgress({
    job,
    isSaving,
    isDownloading,
    labels: {
      processing: t('export.generatingReport'),
      failedPrefix: t('export.reportFailed'),
    },
  });

  const handleReportSelect = (option: ReportOption) => {
    if (isSubmitting || hasActiveExport) return;
    const reportAccess = reportAccessById.get(option.id);
    const isLocked = reportAccess ? !reportAccess.allowed : entitlementsLoading;
    if (isLocked && reportAccess) {
      openUpgradeForReportAccess(reportAccess);
      return;
    }
    setSelectedReportId(option.id);
    setRequestError(null);
    if (option.id === 'genogram.custom_analytical') {
      setSelectedFormat('json');
      return;
    }
    if (option.id === 'genogram.anonymized_teaching') {
      setSelectedFormat('pdf');
      return;
    }
    if (selectedFormat === 'json') {
      setSelectedFormat('docx');
    }
    const csvDisallowed =
      option.id === 'genogram.clinical_case' ||
      option.id === 'genogram.legal_forensic' ||
      option.id === 'genogram.genealogical_historical' ||
      option.id === 'genogram.session_progress_snapshot';
    if (csvDisallowed && selectedFormat === 'csv') {
      setSelectedFormat('docx');
    }
  };

  const availableFormats: Array<'docx' | 'pdf' | 'csv' | 'json'> =
    selectedReportId === 'genogram.custom_analytical'
      ? ['json']
      : selectedReportId === 'genogram.anonymized_teaching'
        ? ['pdf', 'json']
        : selectedReportId === 'genogram.health_risk_summary' ||
            selectedReportId === 'genogram.relationship_dynamics'
          ? ['docx', 'pdf', 'csv']
          : ['docx', 'pdf'];

  const handleStart = async () => {
    if (!selectedReportId) return;
    const option = REPORT_OPTIONS.find((item) => item.id === selectedReportId);
    if (!option) return;

    type StartValidation =
      | { allowed: true }
      | { allowed: false; requireSignIn: true }
      | { allowed: false; upgradeRequired: true; reportAccess: ReportExportAccess }
      | { allowed: false; reason: string };

    const validation: StartValidation = (() => {
      if (!isAuthenticated) {
        return { allowed: false, requireSignIn: true };
      }
      if (entitlementsLoading || !entitlements) {
        return { allowed: false, reason: t('export.entitlementsLoading') };
      }
      const reportAccess = resolveReportAccess(option.id);
      if (!reportAccess.allowed) {
        return { allowed: false, upgradeRequired: true, reportAccess };
      }
      return { allowed: true };
    })();

    if (!validation.allowed) {
      if ('requireSignIn' in validation) {
        onOpenChange(false);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(OPEN_ONBOARDING_EVENT, {
              detail: { page: Pages.SignIn },
            }),
          );
        }
        return;
      }
      if ('upgradeRequired' in validation) {
        openUpgradeForReportAccess(validation.reportAccess);
        return;
      }
      setRequestError(validation.reason);
      return;
    }

    setRequestError(null);
    const hasProgressSince =
      selectedReportId === 'genogram.session_progress_snapshot' && Boolean(progressSinceDate.trim());
    Tracker.track(REPORT_EXPORT.STARTED, {
      reportType: option.id,
      format: selectedFormat,
      hasProgressSince,
      ...(option.reportVariant ? { reportVariant: option.reportVariant } : {}),
    });
    await onStart({
      option,
      format: selectedFormat,
      ...(selectedReportId === 'genogram.session_progress_snapshot' && progressSinceDate.trim()
        ? { progressSince: progressSinceDate.trim() }
        : {}),
    });
  };

  const resolvedError = requestError || error?.message || (isFailed ? statusMessage : null);
  const showSelection = !hasActiveExport && !isFailed;
  const startDisabled =
    isSubmitting ||
    entitlementsLoading ||
    !selectedReportId ||
    isSelectedReportBlocked;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="z-[1001] flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] flex-col overflow-hidden sm:max-h-[85vh] sm:max-w-[420px]"
        overlayClassName="z-[1000]"
        data-testid="tid.export.report.dialog"
        onInteractOutside={(event) => {
          if (hasActiveExport) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>{t('export.generateReport')}</DialogTitle>
          <DialogDescription>
            {showProgress
              ? t('export.reportInProgress')
              : t('export.chooseReportFormat')
            }
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto py-2">
          {resolvedError && (
            <div
              className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              role="alert"
              data-testid="tid.export.report.error"
            >
              <p>{resolvedError}</p>
              {apiUpgradeAccess && (
                <button
                  type="button"
                  className="mt-1 text-xs font-medium underline"
                  onClick={() => openUpgradeForReportAccess(apiUpgradeAccess, apiUpgradeFeature ?? undefined)}
                >
                  {t('export.upgradeToUnlockReport')}
                </button>
              )}
            </div>
          )}

          {showSelection && isSelectedReportBlocked && selectedReportAccess && (
            <div
              className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              role="status"
              data-testid="tid.export.report.upgrade-banner"
            >
              <p>{getReportUpgradeMessage()}</p>
              <button
                type="button"
                className="mt-1 text-xs font-medium text-amber-800 underline"
                onClick={() => openUpgradeForReportAccess(selectedReportAccess)}
              >
                {t('export.upgradeToUnlockReport')}
              </button>
            </div>
          )}

          {showSelection && entitlementsLoading && (
            <p className="mb-3 text-sm text-muted-foreground">{t('export.entitlementsLoading')}</p>
          )}

          {showSelection && (
            <div className="space-y-3">
            <div className="space-y-2">
              <span className="text-sm font-medium text-foreground">{t('export.format')}</span>
              <div
                className={`grid gap-2 ${
                  availableFormats.length === 1
                    ? 'grid-cols-1'
                    : availableFormats.length === 3
                      ? 'grid-cols-3'
                      : 'grid-cols-2'
                }`}
              >
                {availableFormats.map((format) => {
                  const isSelected = selectedFormat === format;
                  const sub =
                    format === 'docx'
                      ? t('export.docxDescription')
                      : format === 'pdf'
                        ? t('export.pdfShort')
                        : format === 'json'
                          ? t('export.jsonShort')
                          : t('export.csvShort');
                  return (
                    <button
                      key={format}
                      type="button"
                      onClick={() => setSelectedFormat(format)}
                      className={`rounded-md border px-3 py-2 text-sm transition-colors ${
                        isSelected ? 'border-primary bg-primary/10 text-foreground' : 'border-border hover:bg-muted text-foreground'
                      } ${availableFormats.length === 3 ? 'col-span-1' : ''}`}
                      disabled={isSubmitting || isSelectedReportBlocked}
                    >
                      {format.toUpperCase()}
                      <div className="text-xs text-muted-foreground">{sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2 overflow-x-hidden pr-1">
              {REPORT_OPTIONS.map((option) => {
                const isSelected = selectedReportId === option.id;
                const reportAccess = reportAccessById.get(option.id);
                const isLocked = reportAccess ? !reportAccess.allowed : entitlementsLoading;
                const showsInlineProgressSince =
                  isSelected && option.id === 'genogram.session_progress_snapshot';
                return (
                  <button
                    key={option.id}
                    type="button"
                    data-testid={`tid.export.report.option.${option.id}`}
                    className={`w-full shrink-0 rounded-md border px-3 py-2 text-left transition-colors ${
                      isSelected ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'
                    } ${isLocked ? 'opacity-80' : ''}`}
                    onClick={() => handleReportSelect(option)}
                    disabled={isSubmitting}
                  >
                    <div className="flex items-center justify-between gap-2 text-sm font-medium text-foreground">
                      <span className="flex items-center gap-1.5">
                        {option.title}
                        {isLocked && (
                          <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
                            <Lock className="h-3 w-3" aria-hidden="true" />
                            {t('export.reportLocked')}
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-muted-foreground">{selectedFormat.toUpperCase()}</span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{option.description}</p>
                    {showsInlineProgressSince && (
                      <div className="mt-3 space-y-1" onClick={(event) => event.stopPropagation()}>
                        <label className="text-sm font-medium text-foreground" htmlFor="report-progress-since">
                          {t('export.progressSinceLabel')}
                        </label>
                        <input
                          id="report-progress-since"
                          type="date"
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                          value={progressSinceDate}
                          onChange={(e) => setProgressSinceDate(e.target.value)}
                          disabled={isSubmitting || isSelectedReportBlocked}
                        />
                        <p className="text-xs text-muted-foreground">{t('export.progressSinceHint')}</p>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            </div>
          )}

          {(showProgress || isFailed) && (
            <div className="py-4 text-center">
              <p className="text-sm text-muted-foreground">{statusMessage}</p>
              {showProgress && !isFailed && (
                <div className="mt-2">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-[width] duration-1000 ease-out"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t pt-3">
          <Button
            variant="ghost"
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else {
                onOpenChange(false);
              }
            }}
          >
            {tCommon('actions.cancel')}
          </Button>
          {showSelection && (
            <Button
              data-testid="tid.export.report.start"
              onClick={handleStart}
              disabled={startDisabled}
            >
              {isSubmitting ? t('export.starting') : t('export.startExport')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
